import type { CompanyDocuments } from 'api';
import type {
  FirmDocumentFolderInput,
  FirmDocumentReferenceWithOptionalFullName,
  FirmDocumentTree,
  FirmDocumentTreeFolder,
} from './firmDocumentTree.types';

type FirmDocumentTreeOrdering = 'filename' | '-filename' | 'uploaded_date' | '-uploaded_date';

interface BuildFirmDocumentTreeOptions {
  showEmptyFolders?: boolean;
}

const compareByName = (itemA: { name: string }, itemB: { name: string }) =>
  itemA.name.localeCompare(itemB.name, undefined, { sensitivity: 'base' });

export const getFirmDocumentReferenceFileName = (
  documentReference: FirmDocumentReferenceWithOptionalFullName
) => {
  if (documentReference.full_filename) {
    return documentReference.full_filename;
  }

  const { filename, file_type: fileType } = documentReference.file;

  return `${filename}.${fileType}`;
};

const compareDocumentReferencesByName = (
  documentReferenceA: FirmDocumentReferenceWithOptionalFullName,
  documentReferenceB: FirmDocumentReferenceWithOptionalFullName
) => {
  const fileNameComparison = getFirmDocumentReferenceFileName(documentReferenceA).localeCompare(
    getFirmDocumentReferenceFileName(documentReferenceB),
    undefined,
    { sensitivity: 'base' }
  );

  return fileNameComparison || documentReferenceA.document_reference_id - documentReferenceB.document_reference_id;
};

const getDocumentReferenceUploadedTimestamp = (
  documentReference: FirmDocumentReferenceWithOptionalFullName
) => {
  const uploadedDate = documentReference.file.uploaded_date;

  if (!uploadedDate) {
    return null;
  }

  const timestamp = Date.parse(uploadedDate);

  return Number.isNaN(timestamp) ? null : timestamp;
};

const getNormalizedDocumentTreeOrdering = (
  ordering?: string | null
): FirmDocumentTreeOrdering => {
  if (
    ordering === 'filename'
    || ordering === '-filename'
    || ordering === 'uploaded_date'
    || ordering === '-uploaded_date'
  ) {
    return ordering;
  }

  return 'filename';
};

const compareDocumentReferencesByUploadedDate = (
  documentReferenceA: FirmDocumentReferenceWithOptionalFullName,
  documentReferenceB: FirmDocumentReferenceWithOptionalFullName,
  isDescending: boolean
) => {
  const uploadedTimestampA = getDocumentReferenceUploadedTimestamp(documentReferenceA);
  const uploadedTimestampB = getDocumentReferenceUploadedTimestamp(documentReferenceB);

  if (uploadedTimestampA === null && uploadedTimestampB === null) {
    return compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
  }

  if (uploadedTimestampA === null) {
    return 1;
  }

  if (uploadedTimestampB === null) {
    return -1;
  }

  const timestampComparison = uploadedTimestampA - uploadedTimestampB;
  const orderedTimestampComparison = isDescending ? -timestampComparison : timestampComparison;

  return orderedTimestampComparison || compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
};

const getDocumentReferenceComparator = (ordering?: string | null) => {
  const normalizedOrdering = getNormalizedDocumentTreeOrdering(ordering);

  if (normalizedOrdering === '-filename') {
    return (
      documentReferenceA: FirmDocumentReferenceWithOptionalFullName,
      documentReferenceB: FirmDocumentReferenceWithOptionalFullName
    ) => -compareDocumentReferencesByName(documentReferenceA, documentReferenceB);
  }

  if (normalizedOrdering === 'uploaded_date' || normalizedOrdering === '-uploaded_date') {
    return (
      documentReferenceA: FirmDocumentReferenceWithOptionalFullName,
      documentReferenceB: FirmDocumentReferenceWithOptionalFullName
    ) => compareDocumentReferencesByUploadedDate(
      documentReferenceA,
      documentReferenceB,
      normalizedOrdering === '-uploaded_date'
    );
  }

  return compareDocumentReferencesByName;
};

const getFolderComparator = (ordering?: string | null) => {
  const normalizedOrdering = getNormalizedDocumentTreeOrdering(ordering);

  if (normalizedOrdering === '-filename') {
    return (folderA: FirmDocumentTreeFolder, folderB: FirmDocumentTreeFolder) => -compareByName(folderA, folderB);
  }

  return compareByName;
};

const createFolderNode = (
  folder: FirmDocumentFolderInput,
  defaultMeasurementDateId?: number
): FirmDocumentTreeFolder | null => {
  if (!folder.id) {
    return null;
  }

  return {
    companyId: folder.companyId,
    files: [],
    id: folder.id,
    isCompanyGroup: folder.isCompanyGroup,
    md_documents: folder.md_documents,
    measurementDateId: folder.measurementDateId ?? defaultMeasurementDateId ?? null,
    name: folder.name,
    parent_folder: folder.parent_folder ?? null,
    subFolders: [],
  };
};

const sortFolderNode = (
  folder: FirmDocumentTreeFolder,
  compareDocumentReferences: ReturnType<typeof getDocumentReferenceComparator>,
  compareFolders: ReturnType<typeof getFolderComparator>
): FirmDocumentTreeFolder => ({
  ...folder,
  files: [...folder.files].sort(compareDocumentReferences),
  subFolders: folder.subFolders
    .map(subFolder => sortFolderNode(subFolder, compareDocumentReferences, compareFolders))
    .sort(compareFolders),
});

const pruneEmptyFolderNode = (folder: FirmDocumentTreeFolder): FirmDocumentTreeFolder | null => {
  const subFolders = folder.subFolders
    .map(pruneEmptyFolderNode)
    .filter((subFolder): subFolder is FirmDocumentTreeFolder => Boolean(subFolder));

  if (!folder.files.length && !subFolders.length) {
    return null;
  }

  return {
    ...folder,
    subFolders,
  };
};

const countFolderNodes = (folders: FirmDocumentTreeFolder[]): number =>
  folders.reduce(
    (folderCount, folder) => folderCount + 1 + countFolderNodes(folder.subFolders),
    0
  );

export const buildFirmDocumentTree = (
  documents: CompanyDocuments | null | undefined,
  ordering?: string | null,
  options: BuildFirmDocumentTreeOptions = {}
): FirmDocumentTree => {
  const files = documents?.files ?? [];
  const folders = documents?.folders ?? [];
  const { showEmptyFolders = true } = options;
  const defaultMeasurementDateId = documents?.measurement_date?.id;
  const folderNodesById = new Map<number, FirmDocumentTreeFolder>();
  const compareDocumentReferences = getDocumentReferenceComparator(ordering);
  const compareFolders = getFolderComparator(ordering);

  folders.forEach(folder => {
    const folderNode = createFolderNode(folder, defaultMeasurementDateId);

    if (folderNode) {
      folderNodesById.set(folderNode.id, folderNode);
    }
  });

  const filesWithoutFolder = files.reduce<FirmDocumentTree['filesWithoutFolder']>((accumulator, documentReference) => {
    const folderId = documentReference.folder ?? null;
    const folderNode = folderId ? folderNodesById.get(folderId) : null;

    if (folderNode) {
      folderNode.files.push(documentReference);
      return accumulator;
    }

    accumulator.push(documentReference);
    return accumulator;
  }, []);

  const rootFolders: FirmDocumentTreeFolder[] = [];

  folderNodesById.forEach(folder => {
    const parentFolderId = folder.parent_folder?.id;
    const parentFolder = parentFolderId ? folderNodesById.get(parentFolderId) : null;

    if (parentFolder) {
      parentFolder.subFolders.push(folder);
      return;
    }

    rootFolders.push(folder);
  });

  const sortedFolders = rootFolders
    .map(folder => sortFolderNode(folder, compareDocumentReferences, compareFolders))
    .sort(compareFolders);
  const visibleFolders = showEmptyFolders
    ? sortedFolders
    : sortedFolders
      .map(pruneEmptyFolderNode)
      .filter((folder): folder is FirmDocumentTreeFolder => Boolean(folder));

  return {
    filesWithoutFolder: [...filesWithoutFolder].sort(compareDocumentReferences),
    folders: visibleFolders,
    totalFileCount: files.length,
    totalFolderCount: showEmptyFolders ? folderNodesById.size : countFolderNodes(visibleFolders),
  };
};
